import type { UnclassifiedAlert } from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

/** Alerts no alertable class explained. Admin DB, cross-tenant. */
export const useUnclassifiedAlerts = (status = "pending") =>
	useQuery({
		queryFn: async () =>
			(
				await apiClient.get<UnclassifiedAlert[]>(
					ENDPOINTS.shadow.plausibility.unclassified,
					{ params: { status } },
				)
			).data,
		queryKey: queryKeys.plausibility.unclassified(status),
	});

/** One alert with its suggestion; idle without an id. */
export const useUnclassifiedAlert = (id: string | undefined) =>
	useQuery({
		enabled: Boolean(id),
		queryFn: async () =>
			(
				await apiClient.get<UnclassifiedAlert>(
					ENDPOINTS.shadow.plausibility.unclassifiedAlert(id ?? ""),
				)
			).data,
		queryKey: queryKeys.plausibility.unclassifiedAlert(id ?? ""),
	});

const refreshAll = (queryClient: ReturnType<typeof useQueryClient>) =>
	queryClient.invalidateQueries({
		queryKey: ["shadow", "plausibility", "unclassified"],
	});

export const useIgnoreUnclassified = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (ids: string[]) =>
			(
				await apiClient.post(ENDPOINTS.shadow.plausibility.unclassifiedIgnore, {
					ids,
				})
			).data,
		onSuccess: () => refreshAll(queryClient),
	});
};

/** Regenerate an alert's suggestion against today's taxonomy. */
export const useRegenerateSuggestion = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) =>
			(
				await apiClient.post<UnclassifiedAlert>(
					ENDPOINTS.shadow.plausibility.unclassifiedSuggest(id),
				)
			).data,
		onSuccess: () => refreshAll(queryClient),
	});
};

/** Mark alerts resolved by the class created for them. */
export const useResolveUnclassified = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: { classId: string; ids: string[] }) =>
			(
				await apiClient.post(
					ENDPOINTS.shadow.plausibility.unclassifiedResolve,
					input,
				)
			).data,
		onSuccess: () => refreshAll(queryClient),
	});
};
